import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
  Button,
  Container,
  Dropdown,
  Header,
  Input,
  Label,
  Loader,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import _ from 'lodash';
import {
  listProjectAssignmentsForAdmin,
  getProjectAssignmentDownloadUrls,
  setProjectApprovalStatus,
} from '../../helpers/api';

const STATUS_COLORS = { PENDING: 'orange', APPROVED: 'green', REJECTED: 'red' };

const ProjectApprovalsPage = ({ userStore }) => {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [journeyFilter, setJourneyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const result = await listProjectAssignmentsForAdmin(statusFilter);
      setRows(result);
    } catch (e) {
      setErr(e.message || 'Failed to load');
    }
  }, [statusFilter]);

  useEffect(() => {
    setRows(null);
    reload();
  }, [reload]);

  const journeyOptions = useMemo(() => {
    if (!rows) return [];
    const uniq = _.uniqBy(rows, 'learningId').map(r => ({
      key: r.learningId,
      value: r.learningId,
      text: r.learningName || r.learningId,
    }));
    return [{ key: '', value: '', text: 'All journeys' }, ...uniq];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (journeyFilter && r.learningId !== journeyFilter) return false;
      if (!q) return true;
      const hay = [
        r.userFirstName,
        r.userLastName,
        r.userEmail,
        r.learningName,
        r.learningId,
        `project${r.assignedProjectIndex}`,
        r.userUid,
        r.id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, journeyFilter]);

  const handleDownload = async (id, which) => {
    try {
      const urls = await getProjectAssignmentDownloadUrls(id);
      const url =
        which === 'project' ? urls.projectUrl : which === 'pre-submission' ? urls.preSubmissionUrl : urls.submissionUrl;
      if (!url) return;
      window.open(url, '_blank');
    } catch (e) {
      setErr(e.message);
    }
  };

  const handleAction = async (id, status) => {
    setErr(null);
    setBusyId(id);
    try {
      await setProjectApprovalStatus(id, status);
      await reload();
    } catch (e) {
      setErr(e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!_.get(userStore, 'user.isAdmin')) {
    return (
      <Container className="mt4">
        <Message error>You must be an admin to view this page.</Message>
      </Container>
    );
  }

  return (
    <Container className="mt4">
      <Header as="h2">
        Project Submissions
        <Header.Subheader>
          Review project submissions across all journeys. {rows ? `${filteredRows.length} of ${rows.length} shown.` : ''}
        </Header.Subheader>
      </Header>

      <Segment basic className="px0">
        <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            icon="search"
            placeholder="Search name / email / journey / project / uid"
            value={search}
            onChange={(_e, { value }) => setSearch(value)}
            style={{ flex: '1 1 320px', minWidth: 240 }}
          />
          <Dropdown
            selection
            search
            placeholder="All journeys"
            options={journeyOptions}
            value={journeyFilter}
            onChange={(_e, { value }) => setJourneyFilter(value)}
            style={{ flex: '0 1 280px', minWidth: 200 }}
          />
          <Button.Group size="small">
            {['PENDING', 'APPROVED', 'REJECTED', ''].map(s => (
              <Button key={s || 'all'} primary={statusFilter === s} basic={statusFilter !== s} onClick={() => setStatusFilter(s)}>
                {s || 'All with submissions'}
              </Button>
            ))}
          </Button.Group>
          <Button size="small" icon="refresh" content="Refresh" onClick={reload} />
        </div>
      </Segment>

      {err && <Message error>{err}</Message>}
      {rows === null ? (
        <Loader active inline="centered" content="Loading…" />
      ) : filteredRows.length === 0 ? (
        <Segment placeholder>
          <Header icon>No submissions match the current filters.</Header>
        </Segment>
      ) : (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Student</Table.HeaderCell>
              <Table.HeaderCell>Journey</Table.HeaderCell>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Pre-assessment</Table.HeaderCell>
              <Table.HeaderCell>Latest submission</Table.HeaderCell>
              <Table.HeaderCell>Attempts</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredRows.map(r => (
              <Table.Row key={r.id}>
                <Table.Cell>
                  <strong>
                    {r.userFirstName} {r.userLastName}
                  </strong>
                  <br />
                  <small>{r.userEmail}</small>
                </Table.Cell>
                <Table.Cell>
                  {r.learningName}
                  <br />
                  <small style={{ color: '#888' }}>{r.learningId}</small>
                </Table.Cell>
                <Table.Cell>
                  project{r.assignedProjectIndex}.zip
                  <br />
                  <Button size="mini" basic onClick={() => handleDownload(r.id, 'project')}>
                    Download project
                  </Button>
                </Table.Cell>
                <Table.Cell>
                  {r.preSubmission ? new Date(r.preSubmission.uploadedAt).toLocaleString() : '—'}
                  <br />
                  {r.preSubmission && (
                    <Button size="mini" basic onClick={() => handleDownload(r.id, 'pre-submission')}>
                      Download pre-assessment
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {r.lastSubmission ? new Date(r.lastSubmission.submittedAt).toLocaleString() : '—'}
                  <br />
                  {r.lastSubmission && (
                    <Button size="mini" basic onClick={() => handleDownload(r.id, 'submission')}>
                      Download submission
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="center">{r.submissions.length}</Table.Cell>
                <Table.Cell>
                  <Label color={STATUS_COLORS[r.approvalStatus] || 'grey'}>{r.approvalStatus}</Label>
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size="mini"
                    color="green"
                    loading={busyId === r.id}
                    disabled={busyId !== null || r.approvalStatus === 'APPROVED'}
                    onClick={() => handleAction(r.id, 'APPROVED')}
                  >
                    Approve
                  </Button>
                  <Button
                    size="mini"
                    color="red"
                    loading={busyId === r.id}
                    disabled={busyId !== null || r.approvalStatus === 'REJECTED'}
                    onClick={() => handleAction(r.id, 'REJECTED')}
                  >
                    Reject
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
};

export default inject('userStore')(observer(ProjectApprovalsPage));
